import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../header/Header';
import Sidebar from '../sidebar/Sidebar';
import Footer from '../footer/Footer';
import PageBreadcrumb from '../breadcrumb/PageBreadcrumb';
import ScrollButtons from '../scroll/ScrollButtons';
import CotinTutorial from '@/core/components/shared/CotinTutorial';
import AssistenteIAWidget from '@/core/components/assistant/AssistenteIAWidget';


function updateLayoutCssVars(footerEl: HTMLElement | null): void {
    const header = document.querySelector('.header') as HTMLElement | null;
    const footer = footerEl ?? (document.querySelector('.footer') as HTMLElement | null);

    const headerHeight = Math.round(
        header?.getBoundingClientRect().height ?? (window.innerWidth <= 768 ? 64 : 75)
    );
    const footerHeight = Math.round(footer?.getBoundingClientRect().height ?? 0);
    const footerTop = footer?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
    const footerVisibleHeight = Math.round(
        Math.min(footerHeight, Math.max(0, window.innerHeight - footerTop))
    );

    document.documentElement.style.setProperty('--app-header-height', `${headerHeight}px`);
    document.documentElement.style.setProperty('--app-footer-height', `${footerHeight}px`);
    document.documentElement.style.setProperty('--app-footer-offset', `${footerVisibleHeight}px`);
}

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const footerRef = useRef<HTMLDivElement>(null);
    const mainRef = useRef<HTMLElement>(null);
    const refreshTimersRef = useRef<number[]>([]);
    const stabilizeIntervalRef = useRef<number | null>(null);
    const stabilizeTimeoutRef = useRef<number | null>(null);

    const showBreadcrumb =
        location.pathname !== '/' &&
        location.pathname !== '/home' &&
        !location.pathname.startsWith('/manutencao') &&
        !location.pathname.startsWith('/maintenance');

    const clearPendingRefreshTimers = useCallback(() => {
        refreshTimersRef.current.forEach((id) => window.clearTimeout(id));
        refreshTimersRef.current = [];
    }, []);

    const clearLayoutStabilizer = useCallback(() => {
        if (stabilizeIntervalRef.current !== null) {
            window.clearInterval(stabilizeIntervalRef.current);
            stabilizeIntervalRef.current = null;
        }
        if (stabilizeTimeoutRef.current !== null) {
            window.clearTimeout(stabilizeTimeoutRef.current);
            stabilizeTimeoutRef.current = null;
        }
    }, []);

    const runUpdate = useCallback(() => {
        updateLayoutCssVars(footerRef.current);
    }, []);

    const scheduleLayoutCssVarRefresh = useCallback(() => {
        runUpdate();
        requestAnimationFrame(() => runUpdate());
        requestAnimationFrame(() => requestAnimationFrame(() => runUpdate()));

        clearPendingRefreshTimers();
        refreshTimersRef.current = [
            window.setTimeout(() => runUpdate(), 60),
            window.setTimeout(() => runUpdate(), 180),
            window.setTimeout(() => runUpdate(), 350),
            window.setTimeout(() => runUpdate(), 700),
        ];

        clearLayoutStabilizer();
        stabilizeIntervalRef.current = window.setInterval(() => runUpdate(), 200);
        stabilizeTimeoutRef.current = window.setTimeout(() => clearLayoutStabilizer(), 3000);
    }, [runUpdate, clearPendingRefreshTimers, clearLayoutStabilizer]);

    const scrollRafRef = useRef<number | null>(null);

    const runUpdateOnScroll = useCallback(() => {
        if (scrollRafRef.current !== null) {
            return;
        }

        scrollRafRef.current = window.requestAnimationFrame(() => {
            scrollRafRef.current = null;
            runUpdate();
        });
    }, [runUpdate]);

    useEffect(() => {
        scheduleLayoutCssVarRefresh();

        const onResize = () => scheduleLayoutCssVarRefresh();

        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', runUpdateOnScroll, { passive: true });
        document.addEventListener('scroll', runUpdateOnScroll, { passive: true, capture: true });

        const mainEl = mainRef.current;
        if (mainEl) {
            mainEl.addEventListener('scroll', runUpdateOnScroll, { passive: true });
        }

        const resizeObserver =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => runUpdate())
                : null;

        const header = document.querySelector('.header');
        const appContainer = document.querySelector('.app-container');
        if (resizeObserver) {
            if (header) resizeObserver.observe(header);
            if (footerRef.current) resizeObserver.observe(footerRef.current);
            if (appContainer) resizeObserver.observe(appContainer);
            if (mainEl) resizeObserver.observe(mainEl);
        }

        const mutationObserver =
            typeof MutationObserver !== 'undefined' && mainEl
                ? new MutationObserver(() => scheduleLayoutCssVarRefresh())
                : null;

        if (mutationObserver && mainEl) {
            mutationObserver.observe(mainEl, {
                childList: true,
                subtree: true,
                attributes: true,
            });
        }

        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', runUpdateOnScroll);
            document.removeEventListener('scroll', runUpdateOnScroll, true);
            if (mainEl) {
                mainEl.removeEventListener('scroll', runUpdateOnScroll);
            }
            if (scrollRafRef.current !== null) {
                window.cancelAnimationFrame(scrollRafRef.current);
                scrollRafRef.current = null;
            }
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            clearPendingRefreshTimers();
            clearLayoutStabilizer();
        };
    }, [scheduleLayoutCssVarRefresh, runUpdate, runUpdateOnScroll, clearPendingRefreshTimers, clearLayoutStabilizer]);

    useEffect(() => {
        scheduleLayoutCssVarRefresh();
    }, [location.pathname, location.search, scheduleLayoutCssVarRefresh]);

    return (
        <div className="app-layout-root">
            <div className="app-container">
                <Header />

                <div className="app-layout">
                    <div className="app-content">
                        <Sidebar />

                        <div className="main-content">
                            <main className="app-main" ref={mainRef}>
                                {showBreadcrumb && <PageBreadcrumb />}
                                {children}
                            </main>
                        </div>
                    </div>

                    <Footer ref={footerRef} />
                </div>

                <ScrollButtons />
                <CotinTutorial />
                <AssistenteIAWidget />
            </div>
        </div>
    );
};

export default AppLayout;
